import './CronExpressionUpdater.css';

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import React, { useState, useEffect, useRef } from 'react';
import { generateClassName, generateSelectField } from '../../b2b-common/helpers';
import FieldComponent from '../../b2b-common/FieldComponent';
import { TextField } from '@mui/material';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';

function generateCronExpression(frequency, interval) {
    switch (frequency) {
        case 'days':
            return `0 0 */${interval} * *`; // Every X days at midnight
        case 'weeks':
            return `0 0 * * ${interval}`; // Every X weeks on Sunday at midnight
        case 'months':
            return `0 0 1 */${interval} *`; // Every X months on the 1st at midnight
        default:
            throw new Error('Invalid frequency');
    }
}

export function parseCronExpression(cronExpression) {
    const parts = cronExpression.split(' ');
    if (parts.length !== 5) {
        throw new Error('Invalid CRON expression');
    }

    if (parts[2].startsWith('*/')) {
        return { frequency: 'days', interval: parseInt(parts[2].substring(2), 10) };
    } else if (parts[4] !== '*') {
        return { frequency: 'weeks', interval: parseInt(parts[4], 10) };
    } else if (parts[3].startsWith('*/')) {
        return { frequency: 'months', interval: parseInt(parts[3].substring(2), 10) };
    } else {
        throw new Error('Invalid CRON expression');
    }
}

const CronExpressionUpdater = ({ initialCronExpression, onUpdate, className = null }) => {
    const { t } = useB2BTranslation('b2b-plans');

    const [enabled, setEnabled] = useState(initialCronExpression !== null);
    const [frequency, setFrequency] = useState('days');
    const [interval, setInterval] = useState(1);

    const prevCronExpression = useRef(initialCronExpression);
    const initialParsed = useRef(false);

    useEffect(() => {
        // console.log('Initial CRON expression:', initialCronExpression);
        if (initialCronExpression && !initialParsed.current) {
            try {
                const { frequency, interval } = parseCronExpression(initialCronExpression);
                setFrequency(frequency);
                setInterval(interval);
                prevCronExpression.current = initialCronExpression;
                initialParsed.current = true;
                // console.log('Parsed CRON expression:', frequency, interval);
            } catch (error) {
                console.error('Failed to parse initial CRON expression:', error);
            }
        }
    }, [initialCronExpression]);

    useEffect(() => {
        const newCronExpression = generateCronExpression(frequency, interval);
        if (enabled) {
            if (newCronExpression !== prevCronExpression.current) {
                onUpdate(newCronExpression);
                prevCronExpression.current = newCronExpression;
            }
        } else {
            if (prevCronExpression.current !== null) {
                onUpdate(null);
                prevCronExpression.current = null;
            }
        }
    }, [enabled, frequency, interval, onUpdate]);

    return (
        <div className={generateClassName('cron-expression-updater', '', className)}>
            <FieldComponent
                label={t('Enable Frequency')}
                component={
                    <div
                        onClick={() => setEnabled(!enabled)}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                        className='cron-expression-updater-checkbox-icon'
                    >
                        {enabled ? <CheckBox /> : <CheckBoxOutlineBlank />}
                    </div>
                }
                className={'cron-expression-updater-checkbox'}
            />
            {enabled && (
                <div className='cron-expression-updater-cron-value'>
                    <TextField
                        type="number"
                        value={interval}
                        onChange={(e) => setInterval(e.target.value)}
                        inputProps={{ min: 1 }}
                        variant="standard"
                        label={t('Interval')}
                        className='cron-expression-updater-interval'
                    />
                    {
                        generateSelectField([
                            { value: 'days', label: t('Days') },
                            { value: 'weeks', label: t('Weeks') },
                            { value: 'months', label: t('Months') }
                        ], {
                            value: frequency,
                            variant: 'standard',
                            label: t('Frequency'),
                            onChange: (e) => setFrequency(e.target.value),
                            className: 'cron-expression-updater-frequency'
                        })
                    }
                </div>
            )}
        </div>
    );
};

export default CronExpressionUpdater;